// Extension templates — shell costs (foundation, structure, roof, exterior,
// glazing, services tie-in) for new-build additions to a property.
// Per-sqm USD baselines that are scaled by the project's currency multiplier.
//
// Cost stack lives in the SHELL — fitout (rooms, finishes) is captured by
// nested Room objects that reuse the existing Room model.

import { effectiveRate } from "@/lib/regionalPresets";
import { extensionShellMultiplier } from "@/lib/constructionTypes";

export const EXTENSION_TYPES = [
  { value: "single_storey", label: "Single-storey ground floor" },
  { value: "double_storey", label: "Double-storey" },
  { value: "loft",           label: "Loft conversion" },
  { value: "garage_conv",    label: "Garage conversion" },
  { value: "knockthrough",   label: "Knock-through (no new sqm)" },
];

// Shell items are PER SQM of FOOTPRINT unless the unit says otherwise.
// All baseRates are USD; effectiveRate() handles currency scaling + library overrides.
const SHELL_TEMPLATES = {
  single_storey: [
    { name: "Foundation",            kind: "mixed",    unit: "sqm", baseRate: 350 },
    { name: "Structural frame",      kind: "mixed",    unit: "sqm", baseRate: 480 },
    { name: "Roof",                  kind: "mixed",    unit: "sqm", baseRate: 380 },
    { name: "Exterior cladding",     kind: "mixed",    unit: "sqm", baseRate: 280 },
    { name: "Glazing",               kind: "material", unit: "sqm", baseRate: 220 },
    { name: "Insulation",            kind: "material", unit: "sqm", baseRate: 90  },
    { name: "Internal lining",       kind: "mixed",    unit: "sqm", baseRate: 180 },
    { name: "Services tie-in",       kind: "labour",   unit: "sqm", baseRate: 220 },
    { name: "Council fees & plans",  kind: "mixed",    unit: "set", baseRate: 4500 },
  ],
  double_storey: [
    { name: "Foundation",            kind: "mixed",    unit: "sqm", baseRate: 380 },
    { name: "Structural frame",      kind: "mixed",    unit: "sqm", baseRate: 720 }, // intermediate floor + heavier load
    { name: "Roof",                  kind: "mixed",    unit: "sqm", baseRate: 280 }, // amortised across 2 floors
    { name: "Exterior cladding",     kind: "mixed",    unit: "sqm", baseRate: 480 }, // 2 storeys of wall per sqm
    { name: "Glazing",               kind: "material", unit: "sqm", baseRate: 320 },
    { name: "Insulation",            kind: "material", unit: "sqm", baseRate: 150 },
    { name: "Internal lining",       kind: "mixed",    unit: "sqm", baseRate: 320 },
    { name: "Stairs",                kind: "mixed",    unit: "set", baseRate: 5500 },
    { name: "Services tie-in",       kind: "labour",   unit: "sqm", baseRate: 320 },
    { name: "Council fees & plans",  kind: "mixed",    unit: "set", baseRate: 7500 },
  ],
  loft: [
    { name: "Structural reinforcement", kind: "mixed",    unit: "sqm", baseRate: 380 },
    { name: "Dormer / roof modification", kind: "mixed",  unit: "sqm", baseRate: 520 },
    { name: "Insulation",            kind: "material", unit: "sqm", baseRate: 110 },
    { name: "Glazing (skylights)",   kind: "material", unit: "sqm", baseRate: 280 },
    { name: "Internal lining",       kind: "mixed",    unit: "sqm", baseRate: 180 },
    { name: "Stairs",                kind: "mixed",    unit: "set", baseRate: 4800 },
    { name: "Services tie-in",       kind: "labour",   unit: "sqm", baseRate: 240 },
    { name: "Council fees & plans",  kind: "mixed",    unit: "set", baseRate: 4500 },
  ],
  garage_conv: [
    { name: "Floor build-up & insulation", kind: "mixed", unit: "sqm", baseRate: 220 },
    { name: "Wall insulation & lining",    kind: "mixed", unit: "sqm", baseRate: 240 },
    { name: "Replace garage door with wall", kind: "mixed", unit: "set", baseRate: 4200 },
    { name: "Glazing",               kind: "material", unit: "sqm", baseRate: 180 },
    { name: "Services tie-in",       kind: "labour",   unit: "sqm", baseRate: 180 },
    { name: "Internal lining",       kind: "mixed",    unit: "sqm", baseRate: 150 },
    { name: "Council fees & plans",  kind: "mixed",    unit: "set", baseRate: 2500 },
  ],
  knockthrough: [
    { name: "Structural beam (RSJ)", kind: "mixed",  unit: "set", baseRate: 3800 },
    { name: "Engineering report",    kind: "labour", unit: "set", baseRate: 1200 },
    { name: "Demolition & removal",  kind: "labour", unit: "set", baseRate: 1500 },
    { name: "Make-good (plaster, paint, flooring tie-in)", kind: "mixed", unit: "set", baseRate: 1800 },
    { name: "Council notification",  kind: "mixed",  unit: "set", baseRate: 600 },
  ],
};

// Indicative typical-mid all-in cost per sqm shown on the type picker, so
// users have a sanity number before committing to a footprint. Fitout is
// excluded — that's the room layer.
const SHELL_PER_SQM_INDICATIVE_USD = {
  single_storey: 2300,
  double_storey: 3400,
  loft:          1900,
  garage_conv:   1300,
  knockthrough:  0, // fixed cost, no sqm
};

export function getExtensionTemplate(type) {
  return SHELL_TEMPLATES[type] || SHELL_TEMPLATES.single_storey;
}

export function indicativeShellRate(type, currency = "USD") {
  return Math.round((SHELL_PER_SQM_INDICATIVE_USD[type] || 0) * (currency === "USD" ? 1 : currencyMultFromEffective(currency)));
}

// Helper — bounce off effectiveRate to derive the regional multiplier without
// importing CURRENCY_MULT directly (keeps this file decoupled).
function currencyMultFromEffective(currency) {
  const baseline = 100;
  const scaled = effectiveRate(baseline, undefined, currency);
  return scaled / baseline;
}

// Build default shell items for a new extension. Like buildDefaultWorkItems
// it scales rates by `overrides[type][item_name]` first, then by the project
// currency multiplier, then by the construction-type multiplier (if a `project`
// with construction settings is supplied).
//
// `overrides` shape: { [item_name]: rate_in_project_currency }
// `project` (optional) — when supplied, applies the construction multiplier
//                       (e.g. brick × 1.25 for Foundation / Structural frame /
//                       Internal lining; roof_type × Roof; etc).
export function buildExtensionShellItems({ type, footprint_sqm, overrides = {}, currency = "USD", project = null }) {
  const tpl = getExtensionTemplate(type);
  const sqm = Math.max(0, Number(footprint_sqm) || 0);
  return tpl.map((item) => {
    const qty = item.unit === "sqm" ? sqm : 1;
    const constructionMult = project ? extensionShellMultiplier(item.name, project) : 1.0;
    const rate = Math.round(effectiveRate(item.baseRate, overrides?.[item.name], currency, item.kind) * constructionMult);
    return {
      id: cryptoRandomId(),
      name: item.name,
      kind: item.kind,
      quantity: qty,
      unit: item.unit,
      rate,
    };
  });
}

export function emptyExtension(type = "single_storey") {
  const meta = EXTENSION_TYPES.find((t) => t.value === type) || EXTENSION_TYPES[0];
  return {
    id: cryptoRandomId(),
    name: meta.label,
    type,
    footprint_sqm: type === "knockthrough" ? 0 : 20,
    storeys: type === "double_storey" ? 2 : 1,
    shell_items: [],
    fitout_rooms: [],
    notes: "",
  };
}

// -------------------------------------------------------------------------
// Fitout "new-build room" extras
// -------------------------------------------------------------------------
// A room added INSIDE an extension shell needs more than the usual renovation
// template (which assumes walls/doors/ceilings already exist). These are the
// extra line items every new-build room should carry: partitions, internal
// doors, a lighting circuit, a ceiling finish and — for habitable rooms —
// at least one window. Regular Rooms tab items (flooring, paint, lighting,
// vanity, appliances, etc) still come from roomTemplates.js.
//
// Room types unlikely to get an external window as part of the fitout are
// listed here — glazing is skipped for them. The extension SHELL already
// accounts for main glazing square-meterage; this item adds a "one window
// per room" baseline for rooms that need one.
const NO_WINDOW_ROOM_TYPES = new Set(["bathroom", "laundry", "hallway"]);

// Base rates in USD per unit. effectiveRate() applies currency + kind
// multipliers so ZAR / AUD / etc all land correctly.
const FITOUT_ROOM_EXTRAS = [
  { name: "Interior partition walls", kind: "mixed",    unit: "sqm",  baseRate: 180, qty: (s) => Math.max(6, Math.round(s * 1.0)) },
  { name: "Ceiling finish",           kind: "mixed",    unit: "sqm",  baseRate: 65,  qty: (s) => Math.max(1, Math.round(s)) },
  { name: "Internal door",            kind: "material", unit: "item", baseRate: 650, qty: () => 1 },
  { name: "Room electrics",           kind: "labour",   unit: "set",  baseRate: 1200, qty: () => 1 },
  { name: "Window / glazing",         kind: "material", unit: "item", baseRate: 450, qty: () => 1, skipFor: NO_WINDOW_ROOM_TYPES },
];

/**
 * Build the extra line items that every new-build fitout room inside an
 * extension shell should carry. Quantities scale with the room's size_sqm.
 *
 * @param {object} opts
 * @param {string} opts.room_type   — bedroom / bathroom / etc
 * @param {number} opts.size_sqm    — floor area of the fitout room
 * @param {object} [opts.overrides] — cost-library overrides for this room type
 * @param {string} [opts.currency]  — project currency for scaling
 * @returns {Array} work-item objects ready to append to room.work_items
 */
export function buildFitoutRoomExtras({ room_type, size_sqm, overrides = {}, currency = "USD" }) {
  const size = Math.max(0, Number(size_sqm) || 0);
  return FITOUT_ROOM_EXTRAS
    .filter((it) => !(it.skipFor && it.skipFor.has(room_type)))
    .map((it) => ({
      id: cryptoRandomId(),
      name: it.name,
      kind: it.kind,
      quantity: it.qty(size),
      unit: it.unit,
      rate: effectiveRate(it.baseRate, overrides?.[it.name], currency, it.kind),
    }));
}

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "ext-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
